import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { mergeAboutDetailsWithLanguages, stripAboutDetails } from './aboutUsConstants';

const EMPTY_FORM = { details: [] };

export default function AboutUsEditModal({ open, initialDetails, languages, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    setError('');
    setForm({
      details: mergeAboutDetailsWithLanguages(languages, initialDetails || []),
    });
  }, [open, initialDetails, languages]);

  function updateDetail(index, key, value) {
    setForm((prev) => {
      const nextDetails = [...prev.details];
      nextDetails[index] = { ...nextDetails[index], [key]: value };
      return { ...prev, details: nextDetails };
    });
  }

  async function handleImageUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const key = `image-${index}`;
    setUploadingKey(key);
    setError('');
    try {
      const url = await adminApi.uploadOssImage(file);
      updateDetail(index, 'image', url);
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploadingKey('');
      event.target.value = '';
    }
  }

  async function handleVideoUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const key = `video-${index}`;
    setUploadingKey(key);
    setError('');
    try {
      const url = await adminApi.uploadOssVideo(file);
      updateDetail(index, 'video', url);
    } catch (err) {
      setError(err.message || '影片上傳失敗');
    } finally {
      setUploadingKey('');
      event.target.value = '';
    }
  }

  function handleReset() {
    setError('');
    setForm({
      details: mergeAboutDetailsWithLanguages(languages, initialDetails || []),
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const details = stripAboutDetails(form.details);
    setSubmitting(true);
    try {
      await adminApi.updateAboutUs({ details });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>修改</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal-panel__body about-us-edit-form" onSubmit={handleSubmit}>
          {error ? <p className="form-message form-message--error">{error}</p> : null}

          <div className="about-us-edit-grid">
            {form.details.map((detail, index) => (
              <section key={detail.lang} className="about-us-edit-card">
                <h4 className="about-us-edit-card__title">{detail._label || detail.lang}</h4>

                <div className="about-us-edit-card__field">
                  <span className="form-label">圖片</span>
                  <div className="carousel-image-upload">
                    {detail.image ? (
                      <img src={resolveAdminMediaUrl(detail.image)} alt="" className="carousel-thumb-preview" />
                    ) : (
                      <div className="carousel-thumb-placeholder">未設定</div>
                    )}
                    <label className="btn btn-success">
                      {uploadingKey === `image-${index}` ? '上傳中…' : '上傳圖片'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={uploadingKey === `image-${index}`}
                        onChange={(e) => handleImageUpload(index, e)}
                      />
                    </label>
                    {detail.image ? (
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => updateDetail(index, 'image', '')}
                      >
                        清除
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="about-us-edit-card__field">
                  <span className="form-label">影片</span>
                  <div className="carousel-image-upload">
                    {detail.video ? (
                      <video src={resolveAdminMediaUrl(detail.video)} controls playsInline className="about-us-card__video" />
                    ) : (
                      <div className="carousel-thumb-placeholder">未設定</div>
                    )}
                    <label className="btn btn-success">
                      {uploadingKey === `video-${index}` ? '上傳中…' : '上傳影片'}
                      <input
                        type="file"
                        accept="video/*"
                        hidden
                        disabled={uploadingKey === `video-${index}`}
                        onChange={(e) => handleVideoUpload(index, e)}
                      />
                    </label>
                    {detail.video ? (
                      <button
                        type="button"
                        className="btn btn-danger-outline btn-sm"
                        onClick={() => updateDetail(index, 'video', '')}
                      >
                        清除
                      </button>
                    ) : null}
                  </div>
                </div>
              </section>
            ))}
          </div>

          <div className="modal-panel__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-success" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-warning" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
